import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { tenantAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Palette, FileText, Save, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ConfiguracionEmpresa = () => {
  const { tenant, updateTenant, isDemo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    cif: '',
    address: '',
    phone: '',
    email: '',
    logo_url: '',
    primary_color: '#1E3A5F',
    accent_color: '#F97316'
  });

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        cif: tenant.cif || '',
        address: tenant.address || '',
        phone: tenant.phone || '',
        email: tenant.email || '',
        logo_url: tenant.logo_url || '',
        primary_color: tenant.primary_color || '#1E3A5F',
        accent_color: tenant.accent_color || '#F97316'
      });
    }
  }, [tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await tenantAPI.update(formData);
      updateTenant(response.data.tenant);
      toast.success('Configuración de empresa actualizada');
    } catch (error) {
      toast.error('Error al guardar la configuración');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="config-empresa-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración de Empresa</h1>
          <p className="text-slate-500 text-sm mt-1">
            Personaliza los datos de tu empresa que aparecerán en el sistema y documentos
          </p>
        </div>
        {isDemo && (
          <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
            Modo Demo
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="datos" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="datos" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Datos</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="documentos" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Documentos</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="datos">
            <Card>
              <CardHeader>
                <CardTitle>Datos de la Empresa</CardTitle>
                <CardDescription>
                  Información general de tu empresa
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre de la Empresa *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Mi Empresa S.L."
                      required
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cif">CIF / NIF</Label>
                    <Input
                      id="cif"
                      name="cif"
                      value={formData.cif}
                      onChange={handleChange}
                      placeholder="B12345678"
                      data-testid="input-cif"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Dirección</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Calle Principal 123, Ciudad"
                    data-testid="input-address"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+34 600 000 000"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="contacto@miempresa.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Personalización Visual</CardTitle>
                <CardDescription>
                  Personaliza los colores de tu marca
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="logo_url">URL del Logo (opcional)</Label>
                  <Input
                    id="logo_url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleChange}
                    placeholder="https://miempresa.com/logo.png"
                    data-testid="input-logo-url"
                  />
                  <p className="text-xs text-slate-500">
                    Ingresa la URL de tu logo. Se mostrará en documentos PDF.
                  </p>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-3">
                    <Label htmlFor="primary_color">Color Primario</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        id="primary_color"
                        name="primary_color"
                        value={formData.primary_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200"
                        data-testid="input-primary-color"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={handleChange}
                        name="primary_color"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Color principal para encabezados y elementos destacados
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="accent_color">Color de Acento</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        id="accent_color"
                        name="accent_color"
                        value={formData.accent_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200"
                        data-testid="input-accent-color"
                      />
                      <Input
                        value={formData.accent_color}
                        onChange={handleChange}
                        name="accent_color"
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Color secundario para botones y acciones
                    </p>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 border rounded-lg bg-slate-50">
                  <p className="text-sm font-medium text-slate-700 mb-3">Vista previa</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                      style={{ backgroundColor: formData.primary_color }}
                    >
                      {formData.name?.charAt(0)?.toUpperCase() || 'E'}
                    </div>
                    <div>
                      <p className="font-bold text-lg" style={{ color: formData.primary_color }}>
                        {formData.name || 'Mi Empresa'}
                      </p>
                      <button 
                        type="button"
                        className="px-3 py-1 rounded text-white text-sm mt-1"
                        style={{ backgroundColor: formData.accent_color }}
                      >
                        Botón de ejemplo
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documentos">
            <Card>
              <CardHeader>
                <CardTitle>Configuración de Documentos</CardTitle>
                <CardDescription>
                  Los datos de empresa configurados se utilizarán automáticamente en:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Facturas PDF</p>
                      <p className="text-sm text-green-600">Nombre, CIF, dirección y datos de contacto</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Presupuestos PDF</p>
                      <p className="text-sm text-green-600">Logo, colores y datos fiscales</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Exportaciones Excel</p>
                      <p className="text-sm text-green-600">Nombre de empresa en el archivo</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Nota:</strong> La configuración avanzada de documentos (texto de pago, 
                    condiciones, etc.) está disponible en la sección de{' '}
                    <a href="/configuracion-documentos" className="text-orange-600 hover:underline">
                      Configuración de Documentos
                    </a>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <Button 
            type="submit" 
            disabled={saving}
            className="gap-2"
            data-testid="save-config-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Guardar Configuración
              </>
            )}
          </Button>
        </div>
      </form>

      {/* MANDO Branding Footer */}
      <div className="text-center py-4 border-t border-slate-200 mt-8">
        <p className="text-xs text-slate-400">
          MANDO · by Leapifyes
        </p>
      </div>
    </div>
  );
};

export default ConfiguracionEmpresa;
